// src/features/developer/json-parser/JsonParser.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Grid, Paper, Tabs, Tab, Button, Stack, Typography, Alert,
    Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@mui/material/styles';
import ReactJson from 'react-json-view';
import StyledFAQ from "../../common/components/StyledFAQ";
import CodeEditor from '../components/CodeEditor';
import { parseJson, formatJson, convertToYaml } from '../utils/jsonUtils';
import { useDebounce } from '../../../hooks/useDebounce';


// Main Component
const JsonParser: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [inputValue, setInputValue] = useState('');
    const [parsedData, setParsedData] = useState<any | null>(null);
    const [outputValue, setOutputValue] = useState('');
    const [yamlValue, setYamlValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    // Settings
    const [indent, setIndent] = useState<number>(2);
    const [strictMode, setStrictMode] = useState<boolean>(false);

    const debouncedInput = useDebounce(inputValue, 300);

    useEffect(() => {
        const processInput = () => {
            const { data, error: parseError } = parseJson(debouncedInput, strictMode);
            setError(parseError);
            setParsedData(data);
            if (data) {
                setOutputValue(formatJson(data, indent));
                setYamlValue(convertToYaml(data));
            } else {
                setOutputValue('');
                setYamlValue('');
            }
        };
        processInput();
    }, [debouncedInput, indent, strictMode]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result;
                setInputValue(typeof text === 'string' ? text : '');
            };
            reader.readAsText(file);
        }
    };

    const handleUrlFetch = async () => {
        const url = prompt("Enter the URL of the JSON file:");
        if (url) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setInputValue(JSON.stringify(data, null, 2));
            } catch (e: any) {
                setError(`Failed to fetch from URL: ${e.message}`);
            }
        }
    };

    const handleCopy = () => {
        let contentToCopy = '';
        if (activeTab === 0) contentToCopy = outputValue;
        if (activeTab === 1) contentToCopy = JSON.stringify(parsedData, null, indent);
        if (activeTab === 2) contentToCopy = yamlValue;
        navigator.clipboard.writeText(contentToCopy);
    };

    const handleDownload = () => {
        const blob = new Blob([outputValue], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'formatted.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const faqs = [
        { q: 'json_parser_faq1_q', a: 'json_parser_faq1_a' },
        { q: 'json_parser_faq2_q', a: 'json_parser_faq2_a' },
        { q: 'json_parser_faq3_q', a: 'json_parser_faq3_a' },
    ];

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 4, overflow: 'hidden' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2, 
                    height: { xs: 'auto', md: '600px' }
                }}>
                    {/* Input Column */}
                    <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        minHeight: { xs: '300px', md: 'auto' }
                    }}>
                        <Stack direction="row" spacing={1} mb={2} justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">{t('json_parser_input_label', { ns: 'tools' })}</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button component="label" size="small" startIcon={<FileUploadIcon />}>
                                    {t('json_parser_upload_file', { ns: 'tools' })}
                                    <input type="file" accept=".json,.txt" hidden onChange={handleFileUpload} />
                                </Button>
                                <Button size="small" startIcon={<LinkIcon />} onClick={handleUrlFetch}>
                                    {t('json_parser_from_url', { ns: 'tools' })}
                                </Button>
                                <Button size="small" color="error" startIcon={<ClearIcon />} onClick={() => setInputValue('')}>
                                    {t('json_parser_clear', { ns: 'tools' })}
                                </Button>
                            </Stack>
                        </Stack>
                        <Box sx={{ flex: 1 }}>
                            <CodeEditor
                                language="json"
                                value={inputValue}
                                onChange={(value) => setInputValue(value || '')}
                                placeholder={t('json_parser_paste_or_drop', { ns: 'tools' })}
                            />
                        </Box>
                        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{error}</Alert>}
                    </Box>

                    {/* Output Column */}
                    <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        minHeight: { xs: '300px', md: 'auto' }
                    }}>
                        <Stack direction="row" spacing={1} mb={2} justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">{t('json_parser_output_label', { ns: 'tools' })}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy} disabled={!parsedData}>
                                    {t('json_parser_copy', { ns: 'tools' })}
                                </Button>
                                <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!parsedData || activeTab !== 0}>
                                    {t('json_parser_download', { ns: 'tools' })}
                                </Button>
                            </Stack>
                        </Stack>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={handleTabChange} aria-label="output format tabs">
                                <Tab label={t('json_parser_tab_formatted', { ns: 'tools' })} />
                                <Tab label={t('json_parser_tab_tree', { ns: 'tools' })} disabled={!parsedData} />
                                <Tab label={t('json_parser_tab_yaml', { ns: 'tools' })} disabled={!parsedData} />
                            </Tabs>
                        </Box>
                        <Box sx={{ flex: 1, width: '100%' }}>
                            {activeTab === 0 && <CodeEditor language="json" value={outputValue} readOnly />}
                            {activeTab === 1 && parsedData && (
                                <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
                                    <ReactJson
                                        src={parsedData}
                                        theme={theme.palette.mode === 'dark' ? 'monokai' : 'rjv-default'}
                                        iconStyle="square"
                                        displayDataTypes={false}
                                        style={{ backgroundColor: 'transparent' }}
                                    />
                                </Box>
                            )}
                            {activeTab === 2 && <CodeEditor language="yaml" value={yamlValue} readOnly />}
                        </Box>
                    </Box>
                </Box>
                {/* Global Settings */}
                <Stack direction="row" spacing={2} mt={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="indent-select-label">{t('json_parser_options_indent', { ns: 'tools' })}</InputLabel>
                        <Select
                            labelId="indent-select-label"
                            value={indent}
                            label={t('json_parser_options_indent', { ns: 'tools' })}
                            onChange={(e) => setIndent(e.target.value as number)}
                        >
                            <MenuItem value={2}>2 Spaces</MenuItem>
                            <MenuItem value={4}>4 Spaces</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={strictMode}
                                onChange={(e) => setStrictMode(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography component="span">{t('json_parser_options_strict_mode', { ns: 'tools' })}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">{t('json_parser_options_strict_mode_desc', { ns: 'tools' })}</Typography>
                            </Box>
                        }
                    />
                </Stack>
            </Paper>

            {/* Information Section */}
            <Box sx={{ my: 15 }}>
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('what_is_json_parser_title', { ns: 'tools' })}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}>
                    {t('what_is_json_parser_desc', { ns: 'tools' })}
                </Typography>

                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('why_choose_us_title', { ns: 'tools' })}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" sx={{ maxWidth: '800px', mx: 'auto', mb: 10 }}>
                    {t('why_choose_us_desc', { ns: 'tools' })}
                </Typography>

                <StyledFAQ
                    title={t('faq_title', { ns: 'tools' })}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default JsonParser;